import { requestData } from "../api-config";

import { SignedPreviewResponse } from "@/types/auth";

const SIGNED_URLS_BATCH_SIZE = 100;

export async function getSignedUrl(
  file_path: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>("gcs/document", "get", {
    searchParams: {
      file_path: file_path,
    },
  });
}

export async function getSignedUrlsBatch(
  filePaths: string[],
): Promise<Record<string, string> | null> {
  if (filePaths.length === 0) return {};

  const responses = await Promise.all(
    Array.from(
      { length: Math.ceil(filePaths.length / SIGNED_URLS_BATCH_SIZE) },
      (_, index) =>
        requestData<Record<string, string>>("gcs/documents", "post", {
          json: {
            file_paths: filePaths.slice(
              index * SIGNED_URLS_BATCH_SIZE,
              (index + 1) * SIGNED_URLS_BATCH_SIZE,
            ),
          },
        }),
    ),
  );

  const signedUrls: Record<string, string> = {};
  for (const response of responses) {
    // A null chunk is a transient request failure (after the network layer's
    // own retries). Fail the whole batch rather than returning a partial map:
    // a partial map would make the failed chunk's paths look like permanent
    // per-file omissions, surfacing a false "Preview unavailable" for them.
    if (!response) return null;
    Object.assign(signedUrls, response);
  }

  return signedUrls;
}

export async function getSignedUrlBase64(
  file_path: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>(
    "gcs/document/base64",
    "get",
    {
      searchParams: {
        file_path: file_path,
      },
    },
  );
}

export async function getSignedDownloadUrl(
  file_path: string,
  filename: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>("gcs/document", "get", {
    searchParams: {
      file_path: file_path,
      response_disposition: `attachment; filename="${filename}"`,
    },
  });
}

export async function downloadFromSignedUrl(
  signedUrl: string,
  _filename: string,
): Promise<void> {
  const link = document.createElement("a");
  link.href = signedUrl;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function getSignedUrlPublic(
  file_path: string,
): Promise<SignedPreviewResponse | null> {
  return await requestData<SignedPreviewResponse>(
    "gcs/document/public",
    "get",
    {
      searchParams: {
        file_path: file_path,
      },
    },
  );
}
